import React, {createRef, ReactElement} from "react";
import {colorBlue, colorGray, colorRed} from "../../../shared/colors";
import {Box, Button, Typography} from "@mui/material";
import {StyledBoxColumn} from "../../../shared/StyledComponents";
import {PAGE_FRAGMENT_WIDTH, PD_LG, PD_MD} from "../../../shared/dimens";
import {
  NonogramDifficultyLevel,
  NonogramGameBoard,
  NonogramGameConfig,
  NonogramGameData,
  NonogramLayout
} from "./types";
import {BaseApp, DIALOG_FLAG_SHOW_CLOSE} from "../../../shared/BaseApp";
import {GameFragment, GameFragmentProps, GameFragmentState} from "../GameFragment";
import {HelpOutlineOutlined, MoreHorizOutlined} from "@mui/icons-material";
import {Action} from "../../../shared/types";
import {GameOverResult, GameOverResultType} from "../types";
import {StringUtil} from "../../../shared/string_util";
import {$KTS} from "../../../shared/keytext";

import {GAME_NONOGRAM} from "../../Games";
import {Game} from "../../types";
import {MINUTE_IN_MILLIS} from "../../../shared/date_util";

export type NonogramGameFragmentProps = GameFragmentProps & {}

enum NonogramGameStatus {
  STATUS_IN_PROGRESS,
  STATUS_INCOMPLETE, // Finished, but NOT solved
  STATUS_COMPLETE, // Finished, and solved
}

type NonogramGameFragmentState = GameFragmentState<NonogramGameConfig, NonogramGameData> & {
  focusIndex: number,
  gameStatus: NonogramGameStatus,
}

export class NonogramGameFragment extends GameFragment<NonogramGameFragmentProps, NonogramGameConfig, NonogramGameData, NonogramGameFragmentState> {

  private readonly canvasParentRef = createRef<HTMLDivElement>();
  private readonly canvasRef = createRef<HTMLCanvasElement>();

  private board: NonogramGameBoard;

  protected getGame(): Game {
    return GAME_NONOGRAM;
  }

  protected onCreateState(): NonogramGameFragmentState {
    return {
      ...super.onCreateState(),
      focusIndex: -1,
      gameStatus: NonogramGameStatus.STATUS_IN_PROGRESS,
    };
  }

  componentDidUpdate(prevProps: Readonly<NonogramGameFragmentProps>, prevState: Readonly<NonogramGameFragmentState>, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);
    if (prevState.gameData !== this.state.gameData) {
      this.board = this.state.gameData.gamePlay.jsonData as NonogramGameBoard;
    }
  }

  protected async fetchOnMount(forceReload?: boolean): Promise<void> {
    const gameConfig = this.state.gameConfig;
    if (!gameConfig) {
      return;
    }
    this.setState({
      gameData: await NonogramGameData.create(gameConfig),
    });
  }

  protected containerContentDidRender() {
    const divElement = this.canvasParentRef.current;
    this.resizeCanvas(divElement.clientWidth, divElement.clientWidth);
    window.onresize = () => {
      this.resizeCanvas(divElement.clientWidth, divElement.clientWidth);
    };
  }

  componentWillUnmount() {
    window.onresize = null;
  }

  protected renderConfigContent(): React.ReactElement {
    return this.renderConfigContentForGame(GAME_NONOGRAM, <>
      {NonogramDifficultyLevel.VALUES.map(level =>
        <Button variant="contained"
                onClick={() => this.setState({gameConfig: new NonogramGameConfig(level)})}>{level.text}</Button>)}
    </>);
  }

  protected renderToolbarTitle(): React.ReactElement {
    return <Typography
      style={{marginLeft: PD_MD,}}>{(this.state.gameData.gamePlay.jsonData as NonogramGameBoard).level.text}</Typography>
  }

  protected renderToolbarButtons() {
    return <>
      <Button onClick={() => BaseApp.CONTEXT.showDialog({flags: DIALOG_FLAG_SHOW_CLOSE}, props => {
        return <StyledBoxColumn style={{padding: PD_LG, width: "100%", maxWidth: PAGE_FRAGMENT_WIDTH}}>
          <Typography variant="h5">How to play Nonogram</Typography>
          <Typography>A logic puzzle with simple rules and challenging solutions.</Typography>
          <ul>
            <li><Typography>You have a grid of squares, which must be either filled in black or marked with X (or left
              empty).</Typography></li>
            <li><Typography>Beside each row of the grid are listed the lengths of the runs of black squares on that
              row.</Typography></li>
            <li><Typography>Above each column are listed the lengths of the runs of black squares in that
              column.</Typography></li>
            <li><Typography>Your aim is to find all black squares.</Typography></li>
            <li><Typography>Click or tap on a square to make it black. Tap again to mark with X. And again to
              clear.</Typography></li>
          </ul>
        </StyledBoxColumn>;
      })}>
        <HelpOutlineOutlined/>
      </Button>
      <Button onClick={(event) => BaseApp.CONTEXT.showActions(event.target as HTMLButtonElement, null, [
        new Action("Check cell", () => this.onCheckCell()),
        new Action("Check board", () => this.onCheckBoard()),
        new Action("Reveal cell", () => this.onRevealCell()),
        new Action("Reveal board", () => this.onRevealBoard()),
        new Action("Reset board", () => this.onResetBoard()),
      ])}>
        <MoreHorizOutlined/>
      </Button>
    </>
  }

  protected renderGameContent(): ReactElement | null {
    if (this.board && this.canvasRef.current) {
      this.renderBoard();
    }
    return <Box style={{display: "flex", flexDirection: "column", alignItems: "center", gap: PD_MD}}>
      <div ref={this.canvasParentRef} style={{width: "100%", maxWidth: 540}}>
        <canvas ref={this.canvasRef} onClick={(event) => {
          if (this.state.gameStatus === NonogramGameStatus.STATUS_COMPLETE) {
            return;
          }
          const rect = this.canvasRef.current.getBoundingClientRect();
          this.onBoardClick(event.clientX - rect.x, event.clientY - rect.y);
        }}/>
      </div>
    </Box>;
  }

  private resizeCanvas(width: number, height: number) {
    const current = this.canvasRef.current;
    if (current.width === width && current.height === height) {
      return;
    }
    current.width = width;
    current.height = height;
    this.forceUpdate();
  }

  private onBoardClick(x: number, y: number) {
    const canvas = this.canvasRef.current;
    const width = canvas.width;
    const height = canvas.height;
    const leftGutter = width / 4;
    const topGutter = height / 4;

    const size = this.board.layout.size;
    const unitSize = (width - leftGutter) / size;
    const sx = Math.floor((x - leftGutter) / unitSize);
    const sy = Math.floor((y - topGutter) / unitSize);
    if (sx >= 0 && sx < size && sy >= 0 && sy < size) {
      const focusIndex = sy * size + sx;
      let value = this.board.board[focusIndex] - 1;
      if (value < -1) {
        value = 1;
      }
      this.board.board[focusIndex] = value;
      this.setState({
        focusIndex: focusIndex,
      });
      this.endInput(true);
    }
  }

  private endInput(didUpdate: boolean) {
    if (didUpdate) {
      // mGameData.writeData(mBoard);
      this.props.gameFragmentListener?.onBoardUpdated();
      this.checkGame();
    }
    this.forceUpdate();
  }

  private onCheckCell() {
    const focusIndex = this.state.focusIndex;
    if (focusIndex < 0) {
      BaseApp.CONTEXT.showToast("No cell selected.");
      return;
    }
    if (this.board.board[focusIndex] < 0) {
      BaseApp.CONTEXT.showToast("Cell is empty.");
      return;
    }
    this.board.checked[focusIndex] = 1;
    this.board.ineligible = true;
    this.forceUpdate();
  }

  private onCheckBoard() {
    for (let i = 0; i < this.board.board.length; i++) {
      this.board.checked[i] = 1;
    }
    this.board.ineligible = true;
    this.forceUpdate();
  }

  private onRevealCell() {
    const focusIndex = this.state.focusIndex;
    if (focusIndex < 0) {
      BaseApp.CONTEXT.showToast("No cell selected.");
      return;
    }
    this.board.board[focusIndex] = this.board.layout.board[focusIndex];
    this.board.checked[focusIndex] = 0;
    this.board.ineligible = true;
    this.forceUpdate();
  }

  private onRevealBoard() {
    BaseApp.CONTEXT.showAlert("Reveal board", "Are you sure you want to reveal the board? The game will end immediately.", [
      new Action("Reveal and end game", () => {
        console.log(this.board.layout.board + ", " + this.board.board.length);
        for (let i = 0; i < this.board.board.length; i++) {
          this.board.board[i] = this.board.layout.board[i];
          this.board.checked[i] = 0;
        }
        this.board.ineligible = true;
        this.endInput(true);
      }),
    ]);
  }

  private onResetBoard() {
    BaseApp.CONTEXT.showAlert("Reset board", "Are you sure you want to reset this game? All your progress will be lost.", [
      new Action("Reset", () => {
        for (let i = 0; i < this.board.board.length; i++) {
          this.board.board[i] = -1;
          this.board.checked[i] = 0;
        }
        this.endInput(true);
        this.props.gameFragmentListener?.onGameReset();
      }),
    ]);
  }

  private checkGame() {
    const gameStatus = this.checkGameStatus();
    switch (gameStatus) {
      case NonogramGameStatus.STATUS_COMPLETE:
        this.state.gameData.gamePlay.finish();
        //audioPlayer.playClip(MyAudioClips.WIN);
        this.props.gameFragmentListener?.onStatusChanged("Game complete.");
        let resultText: string = "You completed %{level} game.";
        switch (this.state.gameConfig.level) {
          case NonogramDifficultyLevel.EASY:
            resultText = StringUtil.format(resultText, $KTS("level", "an easy"));
            break;
          case NonogramDifficultyLevel.MEDIUM:
            resultText = StringUtil.format(resultText, $KTS("level", "a medium"));
            break;
          case NonogramDifficultyLevel.HARD:
            resultText = StringUtil.format(resultText, $KTS("level", "a hard"));
            break;
          case NonogramDifficultyLevel.VERY_HARD:
            resultText = StringUtil.format(resultText, $KTS("level", "a very hard"));
            break;
        }
        const duration = this.state.gameData.getDuration();
        const ineligible = this.board.ineligible;
        this.finishGame(new GameOverResult(GAME_NONOGRAM.id, GameOverResultType.WON, this.board.level.name, this.board.getPoints(duration), 0, resultText, ineligible));
        break;
      case NonogramGameStatus.STATUS_INCOMPLETE:
        //audioPlayer.playClip(MyAudioClips.OH);
        this.props.gameFragmentListener?.onStatusChanged("Game incomplete.");
        //((CommonActivity) getContext()).getTipBar().show(R.string.done_incorrect);
        break;
    }
    this.setState({
      gameStatus: gameStatus,
    });
  }

  // This is based on exact match
  // private checkGameStatus(): NonogramGameStatus {
  //   const size = this.board.layout.size;
  //   for (let i = 0; i < size; i++) {
  //     for (let j = 0; j < size; j++) {
  //       let index = i * size + j;
  //       const n = this.board.board[index];
  //       if (n === -1 && this.board.layout.board[index] > 0) {
  //         return NonogramGameStatus.STATUS_IN_PROGRESS;
  //       }
  //       if ((this.board.board[index] > 0 ? 1 : 0) !== this.board.layout.board[index]) {
  //         return NonogramGameStatus.STATUS_INCOMPLETE;
  //       }
  //     }
  //   }
  //   return NonogramGameStatus.STATUS_COMPLETE;
  // }

  // This is based on row/cols comparison
  private checkGameStatus(): NonogramGameStatus {
    const currLayout = new NonogramLayout(this.board.board, this.board.layout.size);
    if (!this.board.layout.compareEquals(currLayout)) {
      return NonogramGameStatus.STATUS_INCOMPLETE;
    }
    return NonogramGameStatus.STATUS_COMPLETE;
  }

  private renderBoard() {
    if (!this.board) {
      return;
    }
    const canvas = this.canvasRef.current;
    const width = canvas.width;
    const height = canvas.height;
    const leftGutter = width / 4;
    const topGutter = height / 4;
    const scale = width / 600;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, width, height);

    const size = this.board.layout.size;
    const unitSize = (width - leftGutter) / size;
    const innerLineWidth = scale * 2;
    const checkLineWidth = scale * 4;
    ctx.font = "bold " + 32 * scale + "px sans-serif";
    ctx.textBaseline = "middle";
    ctx.textAlign = "center";
    for (let i = 0; i < size; i++) {
      for (let j = 0; j < size; j++) {
        const index = i * size + j;
        const state = this.board.board[index];
        if (state >= 0) {
          if (state === 0) {
            ctx.fillStyle = "black";
            ctx.fillText("✕", leftGutter + unitSize * j + unitSize / 2, topGutter + unitSize * i + unitSize / 2 + scale * 2);
          } else {
            ctx.fillStyle = "black";
            ctx.fillRect(leftGutter + unitSize * j, topGutter + unitSize * i, unitSize, unitSize);
          }
        }
        if (this.board.checked[index] && (state <= 0 ? 0 : 1) !== this.board.layout.board[index]) {
          ctx.strokeStyle = colorRed;
          ctx.lineWidth = checkLineWidth;
          ctx.beginPath();
          ctx.moveTo(leftGutter + unitSize * j, topGutter + unitSize * i);
          ctx.lineTo(leftGutter + unitSize * j + unitSize, topGutter + unitSize * i + unitSize);
          ctx.stroke();
        }
      }
    }

    ctx.fillStyle = "black";
    ctx.font = "bold " + 24 * scale + "px sans-serif";
    for (let i = 0; i < size; i++) {
      const row = this.board.layout.rows[i];
      for (let j = 0; j < row.length; j++) {
        ctx.fillStyle = (i + row.length - j) % 2 === 0 ? GAME_NONOGRAM.color : colorGray;
        const tw = leftGutter / Math.max(2, this.board.layout.getMaxRowLength());
        const tx = leftGutter - (row.length - 1 - j) * tw - tw / 2;
        ctx.fillRect(tx - tw / 2, topGutter + unitSize * i, tw, unitSize);
        ctx.fillStyle = "black";
        ctx.fillText("" + row[j], tx, topGutter + unitSize * i + unitSize / 2 + scale * 2);
      }
    }
    for (let i = 0; i < size; i++) {
      const col = this.board.layout.cols[i];
      for (let j = 0; j < col.length; j++) {
        ctx.fillStyle = (i + col.length - j) % 2 === 0 ? GAME_NONOGRAM.color : colorGray;
        const th = topGutter / Math.max(2, this.board.layout.getMaxColLength());
        const ty = topGutter - (col.length - 1 - j) * th - th / 2;
        ctx.fillRect(leftGutter + unitSize * i, ty - th / 2, unitSize, th);
        ctx.fillStyle = "black";
        ctx.fillText("" + col[j], leftGutter + unitSize * i + unitSize / 2, ty + scale * 2);
      }
    }

    ctx.strokeStyle = colorGray;
    ctx.lineWidth = innerLineWidth;
    for (let i = 1; i < size; i++) {
      ctx.strokeRect(leftGutter + unitSize * i, 0, 0, height);
      ctx.strokeRect(0, topGutter + unitSize * i, width, 0);
    }

    const outerLineWidth = scale * 4;
    ctx.strokeStyle = "black";
    ctx.lineWidth = outerLineWidth;
    ctx.strokeRect(leftGutter + outerLineWidth / 2, 0, 0, height);
    ctx.strokeRect(width - outerLineWidth / 2, 0, 0, height);
    ctx.strokeRect(0, topGutter + outerLineWidth / 2, width, 0);
    ctx.strokeRect(0, height - outerLineWidth / 2, width, 0);
    ctx.strokeRect(leftGutter + outerLineWidth / 2, topGutter + outerLineWidth / 2, width - leftGutter - outerLineWidth, height - topGutter - outerLineWidth);

    const focusLineWidth = scale * 8;
    ctx.strokeStyle = colorBlue;
    ctx.lineWidth = focusLineWidth;
    if (this.state.focusIndex >= 0) {
      const i = Math.floor(this.state.focusIndex / size);
      const j = this.state.focusIndex % size;
      ctx.strokeRect(leftGutter + unitSize * j + focusLineWidth / 2, topGutter + unitSize * i + focusLineWidth / 2, unitSize - focusLineWidth, unitSize - focusLineWidth);
    }
  }
}